import axios from 'axios';
import { getAuth } from '../providers/AppAuthProvider';
import { API_BASE_URL as baseUrl } from 'src/utils/api-url';

const base = 'notification-configs';

async function authHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

/** The language-dependent part of a template. `isEnabled` is separate from the template's `isActive`. */
export type NotificationTemplateTranslation = {
  id?: string;
  languageCode: string;
  subjectTemplate: string;
  bodyTemplate: string;
  isEnabled: boolean;
  createdAt?: string;
  updatedAt?: string;
};

/** Identified by templateCode + channelType; shared attributes live here, languages in translations. */
export type NotificationTemplate = {
  id: string;
  templateCode: string;
  channelType: string;
  notificationTypeCategoryCode: string | null;
  targetAudience: string | null;
  oneSignalTemplateID: string | null;
  description: string | null;
  platforms: string[];
  isActive: boolean;
  version: number;
  translations: NotificationTemplateTranslation[];
  createdAt: string;
  updatedAt: string;
};

export type Platform = 'ANDROID' | 'IOS' | 'WEB';

export const PLATFORMS: Platform[] = ['ANDROID', 'IOS', 'WEB'];

export type ListTemplatesParams = {
  templateCode?: string;
  languageCode?: string;
  targetAudience?: string;
  isActive?: boolean;
  platforms?: Platform[];
  limit?: number;
  offset?: number;
};

export async function listNotificationTemplates(params: ListTemplatesParams = {}) {
  const headers = await authHeaders();
  const query: Record<string, any> = {};
  if (params.templateCode) query.templateCode = params.templateCode;
  if (params.languageCode) query.languageCode = params.languageCode;
  if (params.targetAudience) query.targetAudience = params.targetAudience;
  if (params.isActive !== undefined) query.isActive = params.isActive;
  if (params.platforms?.length) query.platforms = params.platforms;
  query.limit = params.limit ?? 20;
  query.offset = params.offset ?? 0;
  return axios.get<{ items: NotificationTemplate[]; totalCount: number }>(
    `${baseUrl}/${base}/templates`,
    {
      headers,
      params: query,
      // Repeat the key for array values (platforms=IOS&platforms=ANDROID) instead of
      // axios' default bracket notation.
      paramsSerializer: (q) => {
        const search = new URLSearchParams();
        Object.entries(q).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (Array.isArray(value)) value.forEach((v) => search.append(key, String(v)));
          else search.append(key, String(value));
        });
        return search.toString();
      },
    }
  );
}

export type UpsertTemplateTranslation = {
  languageCode: string;
  subjectTemplate?: string;
  bodyTemplate: string;
  isEnabled?: boolean;
};

/** `version` is intentionally absent: the backend owns it and only returns it. */
export type UpsertTemplateBody = {
  templateCode: string;
  channelType: string;
  notificationTypeCategoryCode?: string;
  targetAudience?: string;
  oneSignalTemplateID?: string;
  description?: string;
  platforms?: string[];
  isActive?: boolean;
  translations?: UpsertTemplateTranslation[];
};

function toUpsertBody(data: any): UpsertTemplateBody {
  return {
    templateCode: data.templateCode,
    channelType: data.channelType,
    notificationTypeCategoryCode: data.notificationTypeCategoryCode,
    targetAudience: data.targetAudience,
    oneSignalTemplateID: data.oneSignalTemplateID,
    description: data.description,
    platforms: data.platforms ?? [],
    isActive: data.isActive,
    // languageCode and bodyTemplate are required, so drop rows the user never filled in.
    translations: (data.translations ?? [])
      .filter((tr: any) => tr?.languageCode?.trim() && tr.bodyTemplate?.trim())
      .map((tr: any) => ({
        languageCode: tr.languageCode.trim(),
        subjectTemplate: tr.subjectTemplate,
        bodyTemplate: tr.bodyTemplate,
        isEnabled: tr.isEnabled,
      })),
  };
}

export async function createNotificationTemplate(body: any) {
  const headers = await authHeaders();
  return axios.post(`${baseUrl}/${base}/templates`, toUpsertBody(body), { headers });
}

export async function updateNotificationTemplate(body: any) {
  const headers = await authHeaders();
  return axios.put(`${baseUrl}/${base}/templates`, toUpsertBody(body), { headers });
}

export async function deleteNotificationTemplate(
  templateCode: string,
  languageCode: string,
  channelType: string
) {
  const headers = await authHeaders();
  return axios.delete(`${baseUrl}/${base}/templates`, {
    headers,
    params: { templateCode, languageCode, channelType },
  });
}

export type SubmitNotificationBody = {
  requestType: 'IMMEDIATE' | 'SCHEDULED';
  sendAt?: string;
  payload: {
    recipient: {
      userIds: string[];
      deviceTokenIds: string[];
      targetAudience: string;
    };
    content: {
      templateCode: string;
      variables: string;
      translatedVariables: string;
    };
    metadata: {
      data: string;
    };
    delivery: {
      maxRetryCount: number;
    };
    requestId?: string;
  };
};

export async function submitNotification(body: SubmitNotificationBody) {
  const headers = await authHeaders();
  const requestId = body.payload.requestId?.trim();
  const payload = requestId
    ? { ...body.payload, requestId }
    : (({ requestId: _requestId, ...rest }) => rest)(body.payload);

  return axios.post(`${baseUrl}/${base}/submit`, { ...body, payload }, { headers });
}

// ── Requests ──────────────────────────────────────────────────────────────────

export type NotificationRequestStatus =
  | 'SUBMITTED'
  | 'PENDING'
  | 'PROCESSING'
  | 'CANCELLED'
  | 'FAILED';

export type NotificationRequestType = 'IMMEDIATE' | 'SCHEDULED';

export type NotificationRequest = {
  id: string;
  type: NotificationRequestType;
  status: NotificationRequestStatus;
  sendAt: string | null;
  payload: {
    recipient: {
      userIds: string[] | null;
      deviceTokenIds: string[] | null;
      targetAudience: string | null;
    } | null;
    content: {
      templateCode: string;
      variables: unknown;
      translatedVariables: unknown;
    } | null;
    metadata: { data: unknown } | null;
    delivery: { maxRetryCount: number } | null;
    requestId?: string | null;
  };
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListRequestsParams = {
  status?: NotificationRequestStatus;
  type?: NotificationRequestType;
  limit?: number;
  offset?: number;
};

export async function listNotificationRequests(params: ListRequestsParams = {}) {
  const headers = await authHeaders();
  const query: Record<string, any> = {};
  if (params.status) query.status = params.status;
  if (params.type) query.type = params.type;
  query.limit = params.limit ?? 20;
  query.offset = params.offset ?? 0;
  return axios.get<NotificationRequest[]>(`${baseUrl}/${base}/requests`, {
    headers,
    params: query,
  });
}

export type UpdateNotificationRequestBody = {
  payload: SubmitNotificationBody['payload'];
  sendAt?: string;
};

export async function updateNotificationRequest(id: string, body: UpdateNotificationRequestBody) {
  const headers = await authHeaders();
  const requestId = body.payload.requestId?.trim();
  const payload = requestId
    ? { ...body.payload, requestId }
    : (({ requestId: _requestId, ...rest }) => rest)(body.payload);

  return axios.put(`${baseUrl}/${base}/requests/${id}`, { ...body, payload }, { headers });
}

export async function deleteNotificationRequest(id: string) {
  const headers = await authHeaders();
  return axios.delete(`${baseUrl}/${base}/requests/${id}`, { headers });
}

export async function cancelNotificationRequest(id: string) {
  const headers = await authHeaders();
  return axios.post(`${baseUrl}/${base}/requests/${id}/cancel`, {}, { headers });
}

// ── Categories ────────────────────────────────────────────────────────────────

export type NotificationCategoryTranslation = {
  languageCode: string;
  name: string;
  description: string;
};

export type NotificationCategoryKind = 'PREFERENCE' | 'GROUP';

export type NotificationCategory = {
  id: string;
  categoryCode: string;
  categoryName: string;
  description: string | null;
  defaultValue: boolean;
  enabled: boolean;
  version: number;
  parentId: string | null;
  sortOrder: number;
  kind: NotificationCategoryKind;
  translations: NotificationCategoryTranslation[] | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * Groups come first, each followed by its children; siblings are ordered by sortOrder.
 * Categories whose parent is missing from the page are treated as roots so nothing is dropped.
 */
export function sortCategoryTree(items: NotificationCategory[]): NotificationCategory[] {
  const bySortOrder = (a: NotificationCategory, b: NotificationCategory) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

  const ids = new Set(items.map((c) => c.id));
  const childrenOf = new Map<string | null, NotificationCategory[]>();
  items.forEach((item) => {
    const parent = item.parentId && ids.has(item.parentId) ? item.parentId : null;
    const siblings = childrenOf.get(parent) ?? [];
    siblings.push(item);
    childrenOf.set(parent, siblings);
  });

  const ordered: NotificationCategory[] = [];
  const visit = (parentId: string | null) => {
    (childrenOf.get(parentId) ?? []).sort(bySortOrder).forEach((item) => {
      ordered.push(item);
      visit(item.id);
    });
  };
  visit(null);
  return ordered;
}

/**
 * Each category's ancestors, root first. A parent missing from `items` ends the chain, and so
 * does a cyclic parent chain from bad data.
 */
export function categoryAncestors(
  items: NotificationCategory[]
): Map<string, NotificationCategory[]> {
  const byId = new Map(items.map((c) => [c.id, c]));
  const ancestors = new Map<string, NotificationCategory[]>();
  items.forEach((item) => {
    const chain: NotificationCategory[] = [];
    const seen = new Set([item.id]);
    let parent = item.parentId ? byId.get(item.parentId) : undefined;
    while (parent && !seen.has(parent.id)) {
      chain.unshift(parent);
      seen.add(parent.id);
      parent = parent.parentId ? byId.get(parent.parentId) : undefined;
    }
    ancestors.set(item.id, chain);
  });
  return ancestors;
}

export async function listNotificationCategories(params: { limit?: number; offset?: number } = {}) {
  const headers = await authHeaders();
  return axios.get<{ items: NotificationCategory[]; totalCount: number }>(
    `${baseUrl}/${base}/categories`,
    { headers, params: { limit: params.limit ?? 20, offset: params.offset ?? 0 } }
  );
}

/** Every category, page after page: a sub-category on another page would lose its parent. */
export async function listAllNotificationCategories(): Promise<NotificationCategory[]> {
  const items: NotificationCategory[] = [];
  let totalCount = 0;
  do {
    // Sequential by nature: whether another page is needed is only known from the previous one.
    // eslint-disable-next-line no-await-in-loop
    const res = await listNotificationCategories({ limit: 50, offset: items.length });
    const page = res.data.items ?? [];
    // An empty page stops the loop so a wrong totalCount cannot spin it forever.
    if (page.length === 0) break;
    items.push(...page);
    totalCount = res.data.totalCount ?? 0;
  } while (items.length < totalCount);
  return items;
}

export type ArrangeCategoriesBody = {
  items: { id: string; parentId: string | null; sortOrder: number }[];
};

export async function arrangeNotificationCategories(body: ArrangeCategoriesBody) {
  const headers = await authHeaders();
  return axios.post(`${baseUrl}/${base}/categories/arrange`, body, { headers });
}

export async function getNotificationCategory(id: string) {
  const headers = await authHeaders();
  return axios.get<NotificationCategory>(`${baseUrl}/${base}/categories/${id}`, { headers });
}

export type CreateCategoryBody = {
  categoryCode: string;
  categoryName: string;
  description: string;
  defaultValue: boolean;
  enabled: boolean;
  version: number;
  translations?: NotificationCategoryTranslation[];
  /** Omitted creates a top-level category. The update endpoint cannot move it; arrange does. */
  parentId?: string;
};

export async function createNotificationCategory(body: CreateCategoryBody) {
  const headers = await authHeaders();
  return axios.post(`${baseUrl}/${base}/categories`, body, { headers });
}

export type UpdateCategoryBody = {
  categoryName: string;
  description: string;
  defaultValue: boolean;
  enabled: boolean;
  translations?: NotificationCategoryTranslation[];
};

export async function updateNotificationCategory(id: string, body: UpdateCategoryBody) {
  const headers = await authHeaders();
  return axios.put(`${baseUrl}/${base}/categories/${id}`, body, { headers });
}

export async function deleteNotificationCategory(id: string) {
  const headers = await authHeaders();
  return axios.delete(`${baseUrl}/${base}/categories/${id}`, { headers });
}
